// src/App.js
import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { appRoutes } from "./routes";
import { RequireAuth, AuthProvider } from "./providers/AuthProvider";
import { ThemeProvider, CssBaseline, Tooltip, Typography } from "@mui/material";
import { Chat as ChatIcon } from "@mui/icons-material";
import theme from "./theme";
import Layout from "./components/Layout";
import TopBar from "./components/TopBar";
import ChatWindow from "./components/ChatWindow";
import Footer from "./components/Footer";
import { Box } from "@mui/material";
import { getAuthRole } from "./utils/authSession";

// Auth pages and public pages that should not have sidebar
const authPages = ['/login', '/reset-password', '/forgot-password', '/sign-up', '/register', '/verify-company', '/not-verified', '/pricing', '/change-password', '/enterprise/profile', '/asset-details', '/demo-explorer', '/oauth/callback'];

function AppContent() {
  const location = useLocation();
  const isStandalonePublicPage = location.pathname === '/cloud-platform';
  const isAuthPage = isStandalonePublicPage || authPages.some(path => location.pathname.startsWith(path));
  const [chatOpen, setChatOpen] = useState(false);
  const [chatTrigger, setChatTrigger] = useState(0);
  
  // Get role to check if user is logged in
  const role = getAuthRole();

  if (isAuthPage) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
        {!isStandalonePublicPage && <TopBar />}
        <Box sx={{ pt: isStandalonePublicPage ? 0 : '70px', flex: 1 }}>
          <Routes>
            {appRoutes.map((r) => {
              const element = r.meta?.auth
                ? <RequireAuth role={r.meta.role} pageKey={r.meta.pageKey}>{r.element}</RequireAuth>
                : r.element;
              return <Route key={r.path} path={r.path} element={element} />;
            })}
          </Routes>
        </Box>
        {!isStandalonePublicPage && <Footer />}
      </Box>
    );
  }

  return (
    <>
      <Layout>
        <Routes>
          {appRoutes.map((r) => {
            const element = r.meta?.auth
              ? <RequireAuth role={r.meta.role} pageKey={r.meta.pageKey}>{r.element}</RequireAuth>
              : r.element;
            return <Route key={r.path} path={r.path} element={element} />;
          })}
        </Routes>
      </Layout>

      {/* Floating Chat Button - only show for logged in enterprise admins */}
      {role === 'enterprise_admin' && (
        <>
          <Tooltip title="Open Susha Copilot" placement="left" arrow>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                setChatOpen(true);
                setChatTrigger(t => t + 1);
              }}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 3,
                py: 1.5,
                borderRadius: 50,
                boxShadow: '0 8px 32px rgba(25, 118, 210, 0.4)',
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: 'floatPulse 3s ease-in-out infinite',
                '@keyframes floatPulse': {
                  '0%, 100%': {
                    transform: 'translateY(0) scale(1)',
                    boxShadow: '0 8px 32px rgba(25, 118, 210, 0.4)'
                  },
                  '50%': {
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: '0 12px 40px rgba(25, 118, 210, 0.6)'
                  }
                },
                '&:hover': {
                  transform: 'scale(1.05) translateY(-4px)',
                  boxShadow: '0 12px 48px rgba(25, 118, 210, 0.7)',
                  animation: 'none'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: -3,
                  borderRadius: 50,
                  padding: '2px',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.4), rgba(255,255,255,0.1))',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  opacity: 0.6
                }
              }}
            >
              <ChatIcon sx={{ fontSize: 28 }} />
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  Open Susha Copilot
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9 }}>
                  Read-only, evidence-based answers
                </Typography>
              </Box>
            </Box>
          </Tooltip>

          <ChatWindow
            open={chatOpen}
            trigger={chatTrigger}
            onClose={() => setChatOpen(false)}
          />
        </>
      )}
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}
